<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-custom-blue">Test Panels</h1>
      <button @click="openCreate" class="px-4 py-2 bg-custom-yellow text-white text-sm rounded hover:bg-orange-600">
        + New Panel
      </button>
    </div>

    <div v-if="loading" class="flex justify-center py-16">
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-custom-yellow"></div>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Panel list -->
      <div class="md:col-span-1 space-y-2">
        <div v-if="panels.length === 0" class="text-sm text-gray-400 py-8 text-center">No panels yet.</div>
        <div
          v-for="p in panels" :key="p.id"
          @click="selectPanel(p)"
          class="p-4 border rounded-lg cursor-pointer transition"
          :class="selected?.id === p.id ? 'border-custom-yellow bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'"
        >
          <div class="flex items-center justify-between">
            <span class="font-semibold text-sm text-gray-800">{{ p.name }}</span>
            <span class="text-xs text-gray-400">{{ p.items.length }} test{{ p.items.length !== 1 ? 's' : '' }}</span>
          </div>
          <p v-if="p.description" class="text-xs text-gray-500 mt-1 truncate">{{ p.description }}</p>
        </div>
      </div>

      <!-- Panel detail -->
      <div class="md:col-span-2">
        <!-- Create / Edit form -->
        <div v-if="showForm" class="mb-5 p-5 border border-gray-200 rounded-lg bg-gray-50">
          <h2 class="text-sm font-semibold text-gray-700 mb-4">{{ editingId ? 'Edit Panel' : 'New Panel' }}</h2>
          <div class="grid grid-cols-1 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input v-model="form.name" type="text" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea v-model="form.description" rows="2" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
            </div>
          </div>
          <div class="flex gap-2 justify-end mt-3">
            <button @click="cancelForm" class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100">Cancel</button>
            <button @click="savePanel" :disabled="saving" class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-50">
              {{ saving ? 'Saving…' : (editingId ? 'Update' : 'Create') }}
            </button>
          </div>
        </div>

        <!-- Selected panel detail -->
        <div v-if="selected && !showForm" class="bg-white border border-gray-200 rounded-lg p-5">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-lg font-bold text-custom-blue">{{ selected.name }}</h2>
              <p v-if="selected.description" class="text-sm text-gray-500 mt-0.5">{{ selected.description }}</p>
            </div>
            <div class="flex gap-2">
              <button @click="openEdit(selected)" class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Edit</button>
              <button @click="confirmDelete(selected)" class="px-3 py-1 text-sm bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100">Delete</button>
            </div>
          </div>

          <!-- Items list -->
          <div class="mb-4">
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Included Tests</h3>
            <div v-if="selected.items.length === 0" class="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded">
              No tests added yet — use the form below to add tests.
            </div>
            <table v-else class="w-full text-sm">
              <tbody>
                <tr v-for="item in selected.items" :key="item.id" class="border-b border-gray-100 last:border-0">
                  <td class="py-2 text-gray-800">{{ item.service_name }}</td>
                  <td class="py-2 text-gray-500 text-right pr-4">
                    {{ item.unit_price != null ? `KES ${Number(item.unit_price).toLocaleString()}` : '—' }}
                  </td>
                  <td class="py-2 text-right">
                    <button @click="removeItem(item)" class="text-xs text-red-500 hover:text-red-700">Remove</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Add test to panel -->
          <div class="border-t border-gray-100 pt-4">
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add Test</h3>
            <div class="flex gap-2">
              <v-select
                class="flex-1 text-sm"
                v-model="serviceToAdd"
                :options="serviceOptions"
                :filterable="false"
                placeholder="Search for a lab test…"
                @search="onServiceSearch"
                label="name"
                :loading="serviceSearching"
              >
                <template #no-options="{ searching }">
                  <span v-if="searching">No results</span>
                  <span v-else>Type to search lab tests</span>
                </template>
              </v-select>
              <button
                @click="addItem"
                :disabled="!serviceToAdd || addingItem"
                class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-40"
              >
                {{ addingItem ? '…' : 'Add' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="!showForm && !selected" class="flex items-center justify-center h-48 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg">
          Select a panel on the left to view its details.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import apiClient from '@/services/api'

export default {
  name: 'LabTestPanelsPage',
  components: { vSelect },
  data() {
    return {
      panels:         [],
      loading:        true,
      selected:       null,
      showForm:       false,
      saving:         false,
      editingId:      null,
      form:           { name: '', description: '' },
      serviceToAdd:   null,
      serviceOptions: [],
      serviceSearching: false,
      addingItem:     false,
      searchTimeout:  null,
    }
  },
  async mounted() {
    await this.loadPanels()
  },
  methods: {
    async loadPanels() {
      this.loading = true
      try {
        const res = await apiClient.listLabPanels()
        if (res.success) this.panels = res.data
        if (this.selected) {
          this.selected = this.panels.find(p => p.id === this.selected.id) || null
        }
      } catch { this.$toast.error('Failed to load panels') }
      finally { this.loading = false }
    },
    selectPanel(p) { this.selected = p; this.showForm = false },
    openCreate() { this.editingId = null; this.form = { name: '', description: '' }; this.showForm = true; this.selected = null },
    openEdit(p) { this.editingId = p.id; this.form = { name: p.name, description: p.description || '' }; this.showForm = true },
    cancelForm() { this.showForm = false },
    async savePanel() {
      if (!this.form.name) { this.$toast.error('Name is required'); return }
      this.saving = true
      try {
        const res = this.editingId
          ? await apiClient.updateLabPanel(this.editingId, this.form)
          : await apiClient.createLabPanel(this.form)
        if (res.success) {
          this.$toast.success(this.editingId ? 'Panel updated' : 'Panel created')
          this.showForm = false
          await this.loadPanels()
        } else {
          this.$toast.error(res.message || 'Failed to save')
        }
      } catch { this.$toast.error('Failed to save panel') }
      finally { this.saving = false }
    },
    async confirmDelete(p) {
      if (!confirm(`Delete panel "${p.name}"? This cannot be undone.`)) return
      try {
        const res = await apiClient.deleteLabPanel(p.id)
        if (res.success) {
          this.$toast.success('Panel deleted')
          this.selected = null
          await this.loadPanels()
        } else {
          this.$toast.error(res.message || 'Failed to delete')
        }
      } catch { this.$toast.error('Failed to delete panel') }
    },
    onServiceSearch(search, loading) {
      if (search.length < 2) return
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.serviceSearching = true
      loading(true)
      this.searchTimeout = setTimeout(async () => {
        try {
          const res = await apiClient.searchConsultationFacilityServices({ name: search, type: 'FST-002' })
          if (res.success) this.serviceOptions = res.data
        } catch { /* silent */ }
        finally { this.serviceSearching = false; loading(false) }
      }, 300)
    },
    async addItem() {
      if (!this.serviceToAdd || !this.selected) return
      this.addingItem = true
      try {
        const res = await apiClient.addPanelItem(this.selected.id, this.serviceToAdd.id)
        if (res.success) {
          this.selected.items.push(res.data)
          this.serviceToAdd = null
          this.$toast.success('Test added to panel')
        } else {
          this.$toast.error(res.message || 'Failed to add test')
        }
      } catch { this.$toast.error('Failed to add test') }
      finally { this.addingItem = false }
    },
    async removeItem(item) {
      try {
        const res = await apiClient.removePanelItem(this.selected.id, item.id)
        if (res.success) {
          this.selected.items = this.selected.items.filter(i => i.id !== item.id)
          this.$toast.success('Test removed')
        } else {
          this.$toast.error(res.message || 'Failed to remove')
        }
      } catch { this.$toast.error('Failed to remove test') }
    },
  },
}
</script>
